import React, { useState, useRef, useEffect } from 'react';
import {
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    FormControlLabel,
    Tabs,
    Tab,
    Typography,
    Link,
    useTheme,
    SvgIcon
} from '@mui/material';
import { X, FileText, CircleAlert, CircleCheck } from 'lucide-react-new';
import feeStructureImg from 'src/assets/img/fee_structure.png'
import tosDocument from 'src/views/reports/TermsOfService/Foundry_USA_Pool_Service_Agreement.pdf';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import AcceptTosVersion from 'src/api/AcceptTosVersion';

// Current TOS version - update this when TOS is updated
const CURRENT_TOS_VERSION = '2026-01-27';

const useStyles = makeStyles()(theme => ({
    reportFilterButton: {
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        color: 'var(--accent-9-contrast) !important',
        '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'var(--color-surface-raised)'
        },
        boxShadow: '0 0 0 0 transparent',
        textTransform: 'none',
        padding: 'var(--spacing-md, 8px) var(--spacing-xl, 16px)',
        minHeight: '40px !important'
    },
    selectedTab: {
        '& .Mui-selected': {
            backgroundColor: 'var(--Colors-Background-bg-active, #1F242F);',
        },
        alignItems: 'center !important',
        marginRight: 8,
        '& .MuiTab-root': {
            color: 'var(--accent-9-contrast)'
        }
    }
}));

const TermsOfServiceDialog = ({ open, onClose, }) => {
    const { t } = useTranslation();
    const { loggedInUser, getAccessToken } = useAuth();
    const { classes } = useStyles();
    const [boxChecked, setBoxChecked] = useState(new Set())
    const [agreedToTerms, setAgreedToTerms] = useState(new Set())

    // Check if TOS is valid (either current version or valid MSA)
    const isTosValid = (accepted_tos_version) => {
        if (!accepted_tos_version) return false;

        // Check if it's the current version
        if (accepted_tos_version === CURRENT_TOS_VERSION) return true;

        // Check if it's an MSA format "MSA Expires MM/DD/YYYY"
        const msaMatch = accepted_tos_version.match(/MSA Expires (\d{2}\/\d{2}\/\d{4})/);
        if (msaMatch) {
            const expirationDate = new Date(msaMatch[1]);
            return expirationDate > new Date();
        }

        return false;
    };

    // Find the first group that needs acceptance (sorted alphabetically)
    const getInitialTab = () => {
        const sortedGroups = [...loggedInUser.groups].sort((a, b) => a.group.name.localeCompare(b.group.name));
        const firstNeedingAcceptance = sortedGroups.find(groupItem => {
            const isMiningAccountOwner = groupItem.userRole.roleName === 'mining_account_owner';
            const tosIsValid = isTosValid(groupItem.group.accepted_tos_version);
            return isMiningAccountOwner && !tosIsValid;
        });
        return firstNeedingAcceptance?.group.name || loggedInUser?.groups[0]?.group.name;
    };

    const [selectedTab, setSelectedTab] = useState(getInitialTab());
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(new Set());

    const contentRef = useRef(null);
    const tabsRef = useRef(null);
    const theme = useTheme();

    const handleTabsChange = (event, value) => {
        setSelectedTab(value);

        if (!agreedToTerms.has(value)) {
            setHasScrolledToBottom(prevSet => {
                const newSet = new Set(prevSet);
                newSet.delete(value);
                return newSet;
            });

            setBoxChecked(prevSet => {
                const newSet = new Set(prevSet);
                newSet.delete(value);
                return newSet;
            });
        } else {
            setBoxChecked(prevSet => {
                const newSet = new Set(prevSet);
                newSet.add(value);
                return newSet;
            });
        }

        scrollToTop();
    };

    const handleToggleSet = (value) => {
        setBoxChecked(prevSet => {
            const newSet = new Set(prevSet);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return newSet;
        });
    };

    const handleAgreeClick = async (value) => {
        // Find the current group to get the group ID
        const currentGroup = loggedInUser.groups.find(g => g.group.name === value);
        if (!currentGroup) return;

        try {
            // Get access token
            const { tokenAvailable, accessToken } = await getAccessToken();
            if (!tokenAvailable) {
                console.error('No access token available');
                return;
            }

            // Make API call to accept TOS
            await AcceptTosVersion(currentGroup.group.id, CURRENT_TOS_VERSION, accessToken);

            // Calculate if we should close BEFORE updating any state
            const newAgreedSet = new Set(agreedToTerms);
            newAgreedSet.add(value);

            // Find the next tab that needs acceptance
            const sortedGroups = [...loggedInUser.groups].sort((a, b) => a.group.name.localeCompare(b.group.name));
            const nextTab = sortedGroups.find(groupItem => {
                const isMiningAccountOwner = groupItem.userRole.roleName === 'mining_account_owner';
                const tosIsValid = isTosValid(groupItem.group.accepted_tos_version);
                const needsAcceptance = isMiningAccountOwner && !tosIsValid && !newAgreedSet.has(groupItem.group.name);
                return needsAcceptance;
            });

            if (nextTab) {
                // Update state and move to next tab that needs acceptance
                setAgreedToTerms(newAgreedSet);
                setSelectedTab(nextTab.group.name);
                setHasScrolledToBottom(prevScrollSet => {
                    const newScrollSet = new Set(prevScrollSet);
                    newScrollSet.delete(nextTab.group.name);
                    return newScrollSet;
                });
                setBoxChecked(prevCheckSet => {
                    const newCheckSet = new Set(prevCheckSet);
                    newCheckSet.delete(nextTab.group.name);
                    return newCheckSet;
                });
                scrollToTop();
            } else {
                // All tabs are agreed, close the dialog WITHOUT updating state
                // This prevents any state updates after we've decided to close
                onClose();
            }
        } catch (error) {
            console.error('Error accepting TOS:', error);
            // TODO: Show error message to user
        }
    }

    const scrollToTop = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({
                top: 0,
                behavior: 'instant'
            });
        }
    };

    const handleScroll = (e) => {
        const element = e.target;
        const bottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;

        if (bottom) {
            setHasScrolledToBottom(prevSet => {
                const newSet = new Set(prevSet);
                newSet.add(selectedTab);
                return newSet;
            });
        }
    };

    useEffect(() => {
        if (!open || !selectedTab) return;

        const timer = setTimeout(() => {
            if (contentRef.current) {
                const element = contentRef.current;
                const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;

                if (isAtBottom) {
                    setHasScrolledToBottom(prevSet => {
                        const newSet = new Set(prevSet);
                        newSet.add(selectedTab);
                        return newSet;
                    });
                }
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [selectedTab, open]);

    // Add mouse wheel scroll support for tabs
    useEffect(() => {
        const tabsElement = tabsRef.current;
        if (!tabsElement) return;

        const handleWheel = (e) => {
            const scrollContainer = tabsElement.querySelector('.MuiTabs-scroller');
            if (scrollContainer) {
                e.preventDefault();
                scrollContainer.scrollLeft += e.deltaY;
            }
        };

        tabsElement.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            tabsElement.removeEventListener('wheel', handleWheel);
        };
    }, []);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: '70vh',
                    borderRadius: "12px",
                    backgroundColor: 'var(--neutral-3)',
                    m: 0,
                    maxHeight: { xs: 'calc(100vh - 32px)', sm: '70vh' },
                    width: { xs: 'calc(100vw - 32px)', sm: '100%' },
                    overflowX: 'hidden'
                }
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                '& .MuiDialog-paper': {
                    overflowX: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ padding: { xs: 2, sm: 3 }, overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            backgroundColor: "#202939",
                            width: "48px",
                            height: "48px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "24px",
                            flexShrink: 0,
                            mt: { xs: '8px', sm: 0 }
                        }}>
                            <FileText />
                        </Box>
                        <Typography sx={{
                            fontWeight: "600",
                            fontSize: "18px",
                            zIndex: 1,
                            mt: { xs: '8px', sm: 0 }
                        }}>
                            Terms and conditions
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 3,
                            cursor: 'pointer',
                            flexShrink: 0,
                            mt: { xs: '8px', sm: 0 }
                        }}
                        onClick={onClose}
                    >
                        <X size={30} color="#70707B" />
                    </Box>
                </Box>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 480,
                        height: 480
                    }}
                >
                    <svg
                        width="240"
                        height="240"
                        viewBox="0 0 240 240"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="48" cy="48" r="48" stroke="#202939" />
                        <circle cx="48" cy="48" r="80" stroke="#202939" />
                        <circle cx="48" cy="48" r="112" stroke="#202939" opacity={0.6} />
                        <circle cx="48" cy="48" r="144" stroke="#202939" opacity={0.4} />
                        <circle cx="48" cy="48" r="176" stroke="#202939" opacity={0.2} />
                    </svg>
                </div>
                {loggedInUser.groups.length > 1 && <Box ref={tabsRef} sx={{ position: 'relative', zIndex: 2, mt: '12px' }}>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabsChange}
                        variant="scrollable"
                        className={classes.selectedTab}
                        scrollButtons={false}
                        indicatorColor='transparent'
                    >
                        {
                            [...loggedInUser.groups].sort((a, b) => a.group.name.localeCompare(b.group.name)).map((groupItem, index) => {
                                const isMiningAccountOwner = groupItem.userRole.roleName === 'mining_account_owner';
                                const tosIsValid = isTosValid(groupItem.group.accepted_tos_version);
                                const hasAgreed = agreedToTerms.has(groupItem.group.name);
                                const isDisabled = !isMiningAccountOwner || tosIsValid || hasAgreed;
                                const showAlert = isMiningAccountOwner && !tosIsValid && !hasAgreed;
                                const isSelected = selectedTab === groupItem.group.name;
                                return (
                                    <Tab
                                        disabled={isDisabled}
                                        key={groupItem.group.id}
                                        value={groupItem.group.name}
                                        label={<span style={{ opacity: isDisabled ? 0.4 : 1 }}>{groupItem.group.name}</span>}
                                        icon={<span style={{ opacity: isDisabled ? 0.4 : 1 }}>{showAlert ? <CircleAlert color="var(--color-text-danger)" /> : <CircleCheck color="var(--color-text-success)" />}</span>}
                                        iconPosition="end"
                                        className={classes.reportFilterButton}
                                        style={{
                                            ...(index === 0
                                                ? { borderRadius: 'var(--radius-l) 0 0 var(--radius-l)' }
                                                : index === loggedInUser.groups.length - 1 ? { borderRadius: '0 var(--radius-l) var(--radius-l) 0', borderLeft: '0px solid var(--color-border)' }
                                                    : { borderLeft: '0px solid var(--color-border)' }
                                            ),
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            backgroundColor: isSelected ? 'var(--Colors-Background-bg-active, #1F242F)' : 'var(--color-surface)',
                                            position: 'relative',
                                            zIndex: 2
                                        }}
                                    />
                                )
                            })
                        }
                    </Tabs>
                </Box>}
            </DialogTitle>


            <DialogContent
                sx={{
                    backgroundColor: '#fff',
                    color: "#000",
                    p: { xs: 2, sm: 3 },
                    mx: { xs: 2, sm: 6 },
                    my: 0,
                    zIndex: 2,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    maxWidth: '100%'
                }}
                ref={contentRef}
                onScroll={handleScroll}
            >
                <Box sx={{ maxWidth: '100%', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                    <Typography variant="body2" sx={{ textAlign: "center", mb: 2, mt: 2 }}>
                        Foundry USA Pool™ Terms of Service
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                        Effective as of January 27, 2026
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        The ownership and operation rights of the services provided by the Foundry USA Pool™ ("Pool") are owned by Foundry Digital LLC ("Foundry"). The Foundry
                        Terms of Service specified herein ("Terms"), along with Foundry's Terms and Conditions, as may be amended from time to time ("Conditions"), Privacy Policy ("Privacy
                        Policy") and Foundry USA Pool's Payout Methodology ("Pool's Payout Methodology") are the relevant rights and obligations, which shall be deemed to have been read and
                        accepted by anyone who has access and/or uses the Pool ("User").
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        By accessing and using the Pool, User accepts and agrees to the Terms, Conditions, and Privacy Policy (collectively, the "Service Agreement"). As the
                        operator of the Pool, Foundry shall provide a mining Pool (as defined below) to User under the Service Agreement.
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 3 }}>
                        User agrees that Foundry will have the right to modify the Service Agreement at any time without prior notice, except as otherwise required by applicable
                        law. Any notice provided may be delivered via email and/or via your User account. Further, User agrees to be solely responsible for reviewing the Service Agreement and/or any modifications thereto. If User does not agree to the Service
                        Agreement and/or any of its modifications, then User shall cease to use and will not be allowed further access to the Pool and Service.
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        1. Foundry Terms and Conditions
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        Foundry's Terms and Conditions ("Conditions") are available at{' '}
                        <Link href="https://foundrydigital.com/terms-and-conditions" target="_blank" rel="noopener">
                            https://foundrydigital.com/terms-and-conditions
                        </Link>
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        2. Privacy and Protection
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        Foundry places great importance on the protection of User's personal information. When using the Pool and Service provided by Foundry, User agrees that
                        Foundry will collect, store, use, disclose and protect User's personal information in accordance with the Privacy Policy, as may be updated from time to time,
                        available at{' '}
                        <Link href="https://foundrydigital.com/privacy-policy" target="_blank" rel="noopener">
                            https://foundrydigital.com/privacy-policy
                        </Link>
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        3. Pool's Payout Methodology
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        Foundry USA Pool's Payout Methodology ("Pool's Payout Methodology") is available at{' '}
                        <Link href="https://pool-faq.foundrydigital.com/what-is-foundry-usa-pools-payout-methodology" target="_blank" rel="noopener">
                            https://pool-faq.foundrydigital.com/what-is-foundry-usa-pools-payout-methodology
                        </Link>
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        4. Definitions
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        For purposes of this Service Agreement, all capitalized terms that are not otherwise defined herein shall have the meanings set forth in the Pool's Payout
                        Methodology.
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        5. Services
                    </Typography>
                    <Box sx={{ pl: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            a. Foundry uses its own system, through the Internet and other means to provide User with a digital currency mining Pool and other services/products that
                            may be added based on the Pool site ("Service"). For the avoidance of doubt, the Pool and Service shall not include wallet or custodial services from Foundry to User.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            b. User shall be responsible for preparing the necessary equipment and bear the expenses related to using such necessary equipment to participate in the
                            Pool and Service.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            c. User hereby authorizes Foundry to be fully responsible for disposal and distribution of the profit from such value-added Service.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            d. Foundry reserves the right to modify, suspend, interrupt, and/or terminate the Service at any time without informing User and without liability to
                            User or any third party not directly related.
                        </Typography>
                    </Box>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        6. User Representations
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        By accessing or using the Pool and the Service, the User represents and warrants that they meet the following qualifications:
                    </Typography>
                    <Box sx={{ pl: 4, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>a. Eligibility</strong> - The User is at least 18 years old and has the legal capacity to enter into a binding agreement. If the User is
                            accessing the platform on behalf of an organization, they have the necessary authority to do so.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>b. Compliance with Laws</strong> - The User agrees to comply with all applicable laws, regulations, and third-party agreements, and not use the
                            platform for any unlawful or prohibited activities, or from any country, territory, or jurisdiction that is subject to U.S. embargoes, comprehensive sanctions, or other trade restrictions.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>c. Account Information</strong> - The User agrees to provide accurate, up-to-date, and complete information when creating an account or using the
                            Service and agrees to promptly update such information as necessary to keep it accurate and complete.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>d. Restrictions</strong> - The User must not access the Service if they are restricted from doing so in any jurisdiction or under any applicable
                            laws or regulations. Foundry reserves the right to refuse or terminate access to the platform if the User does not meet these qualifications.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>e. Wallet</strong> - User acknowledges that, unless otherwise consented to in writing by Foundry, any digital asset wallet it connects to the
                            Pool must be solely owned, controlled, and accessible by the User. Failure to comply with this provision may result in immediate suspension or termination of
                            participation in the Pool.
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        Foundry reserves the right to modify or update the qualification requirements from time to time, and the User agrees to comply with such changes.
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        7. User Rights and Obligations
                    </Typography>
                    <Box sx={{ pl: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            a. Prior to entering and using the Pool and Service, User agrees it must first successfully complete the client onboarding process required by Foundry.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            b. User agrees to provide legal, true, accurate and detailed personal information to Foundry, and update such information as needed.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            c. User shall comply with all applicable laws, rules, and regulations including, but not limited to, those related to the use of the Pool and Service.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            d. User acknowledges and agrees that it is using the Pool and Service at its own risk.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            e. The User acknowledges and agrees that it can choose whether or not to apply hashrate to individual block templates.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            f. User acknowledges and agrees that the Pool is in no way obligated to allocate to Users the actual value derived from specific block rewards earned by
                            the Pool so long as payouts to Users are made in accordance with Section 10 herein.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            g. In the event User's access and/or rights to the Pool and Service have been discontinued for any reason, then User is solely responsible for settling
                            the remaining balances left in its account. Foundry shall use commercially reasonable efforts to assist User with settling any remaining balances in User's account.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1, pl: 2, fontStyle: 'italic' }}>
                            For the avoidance of doubt, Foundry shall not be responsible or liable to User for any balances remaining in User's account three (3) months after User's
                            access and/or rights to the Pool and Service have been discontinued (regardless of whether the balances were left in User's account intentionally).
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            h. User agrees (i) not to direct any hashrate to the Pool that comes from a jurisdiction sanctioned or restricted by the U.S. Department of the
                            Treasury's Office of Foreign Assets Control (OFAC) ("Sanctioned Jurisdiction"), and (ii) accepts full responsibility and shall indemnify and hold Foundry harmless for
                            any and all liabilities resulting from a breach of this provision.
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                i. User acknowledges and agrees that (i) Foundry shall take any and all steps it deems necessary, in its sole and absolute discretion, to remove any
                                hashrate directed at the Pool that is received from a Sanctioned Jurisdiction, and (ii) User shall take any and all steps deemed necessary by Foundry, in its sole and
                                absolute discretion, to assist Foundry with the removal of such hashrate.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                ii. User agrees that, in the event User sells and/or transfers any Mining Equipment, that User shall take any and all steps which Foundry deems
                                necessary, in its sole and absolute discretion, to remove the Mining Equipment's connection to the Pool prior to such sale and/or transfer, including but not limited
                                to (i) deleting User's Pool account credentials, (ii) deleting User's configurations to the Mining Equipment, and/or (iii) performing a complete factory reset on the
                                Mining Equipment. For purposes of this Service Agreement, "Mining Equipment" shall mean User's cryptocurrency mining equipment.
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        8. Use of Aggregated Data
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        By using the Services, User agrees that we may collect, use, and analyze data derived from your interactions with the Services, including but not limited
                        to usage patterns, preferences, and performance metrics. Foundry retains sole discretion in using collected data in aggregated and anonymized forms for its business
                        purposes. To the extent that any collected data constitutes personal information, Foundry will collect, store, use, disclose, and protect User's personal information
                        in accordance with the Privacy Policy.
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        9. Third Party Services
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        If User provides services to any third parties who may benefit, whether directly or indirectly, from payments under this Agreement, User agrees to notify
                        Foundry in writing: (a) during the client onboarding process; and (b) immediately upon any changes to the offering of third-party products and services. Furthermore, User
                        agrees to provide any necessary information and documentation reasonably requested by Foundry to perform any necessary due diligence on third parties, at Foundry's sole discretion.
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        10. Confidentiality
                    </Typography>
                    <Box sx={{ pl: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            a. User agrees not to disclose any Confidential Information from the Pool and/or Service. "Confidential Information" includes (but is not limited to)
                            information regarding Foundry's Pool, Service, documentation, software, trade secrets embodied therein and any other written or electronic information that is either
                            (i) marked as confidential and/or proprietary, or which is accompanied by written notice that such information is confidential and/or proprietary, or (ii) not marked
                            or accompanied by notice that it is confidential and/or proprietary but which, if disclosed to any third party, could reasonably and foreseeably cause competitive harm
                            to the owner of such information.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            b. Confidential Information shall not include information which is: (i) publicly available, (ii) lawfully obtained by a party from third parties without
                            restrictions on disclosure, or (iii) independently developed by a party without reference to or use of the Confidential Information.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            If the User is requested or required (by deposition, interrogatories, requests for information or documents, subpoena, civil investigative demand or
                            similar process, hereinafter "Legal Demand") to disclose any confidential information of Foundry, then, to the extent User is permitted under applicable law or
                            regulation, it shall provide Foundry with prompt written notice of such request(s), and provide commercially reasonable assistance to Foundry in obtaining a protective
                            order preventing or limiting the disclosure or requiring that the confidential information of Foundry so disclosed be used only for the purposes for which the law or
                            regulation required, or for which the order was issued. User agrees that, in the event Foundry is unsuccessful in obtaining such protective order, that User shall only
                            disclose the minimum amount of information required to satisfy such Legal Demand.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            c. For the avoidance of doubt, the service organization controls report ("SOC Report") shared with User by Foundry shall be considered "Confidential
                            Information" as defined herein, and as such, User agrees not to disclose the SOC Report to any third-party (regardless of whether or not such third-party is separately
                            a user of the Pool and/or Service) without the prior and express written consent of Foundry. For purposes herein, a "third party" shall not include User's affiliates,
                            subsidiaries, officers, directors, employees, contractors, attorneys, accountants, bankers or consultants ("Authorized Representatives") with a need to know of such
                            SOC Report, provided, however, that such Authorized Representatives shall be under an obligation of confidentiality and non-use of the SOC Report at least as strict as
                            set out in this Agreement.
                        </Typography>
                    </Box>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        11. Payouts to Users
                    </Typography>
                    <Box sx={{ pl: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>a. Payouts</strong> - User is entitled to compensation regardless of whether the Pool successfully records a block to the Bitcoin blockchain. The
                            Daily Earnings of each User is calculated and paid in accordance with the Pool's Payout Methodology:
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                i. <strong>Bitcoin (BTC)</strong> - The Pool shall issue payments using the Full-Pay-Per-Share ("FPPS") payout scheme, where the User shall receive the
                                expected value from the block reward plus the transaction fee reward.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                ii. <strong>Missed Payments</strong> - If Foundry is delayed in making any payment to User, Foundry shall cure such payment issue within seven (7) days
                                after User provides Foundry with notice and evidence of such missed payment.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                iii. <strong>Disruption in Service</strong> - Foundry may, at its sole discretion, offer User payment or credits in the event of a disruption in
                                Service.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                iv. <strong>Overpayment</strong> - In the event that Foundry overpays the User due to an error or miscalculation, User agrees that Foundry will
                                automatically offset the overpayment against future payouts owed to the User.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                v. <strong>Emergency Protective Actions</strong> - Foundry reserves the right to adjust the FPPS payout methodology without notice, in emergency
                                situations (as commercially reasonable), as necessary to protect the Pool.
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>b. Modification or Suspension of Payouts</strong> - In the event that malicious or inadvertent activity has either occurred or Foundry reasonably
                            believes has occurred on the Bitcoin network, where such malicious or inadvertent activity has a material impact on the Bitcoin mining transaction fees, then Foundry
                            may modify or suspend payouts until Foundry reasonably believes such malicious or inadvertent activity no longer exists.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>c. Minimum Threshold</strong> - At account closure, subaccounts will be paid out only when their balance meets the minimum threshold of 2,730 sats.
                        </Typography>
                    </Box>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        12. Pool Fees
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
                        Foundry reserves the right to charge the User pool fees at any time in accordance with Exhibit A of the Terms. Further, Foundry may update the pool fees
                        Exhibit A of the Terms in its sole and absolute discretion, and in such event, shall make commercially reasonable efforts to notify User prior to any such updates.
                    </Typography>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        13. Contract Term and Termination
                    </Typography>
                    <Box sx={{ pl: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            a. User's access and usage rights to the Pool and Service shall commence upon the successful completion by User of the client onboarding process required
                            by Foundry (the "Commencement Date") and continue until 23:59:59 UTC on the Commencement Date and shall automatically be renewed for successive 24-hour periods (the
                            period between 00:00:00 UTC and 23:59:59 UTC) (each, a "Contract Day") unless terminated in accordance with the Terms set forth herein (the "Contract Term"). For
                            avoidance of doubt, (i) User's access and usage rights to the Pool and Service are not contingent on delivering any minimum amount of User hashrate to the Pool during
                            any Contract Day of the Contract Term and (ii) User is not bound to continue using the Pool in accordance with any other agreement with Foundry.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            b. User's access and usage rights to the Pool and Service may be terminated for any reason, without penalty, by either Foundry or User by providing one
                            Contract Day's prior written notice to the other party.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            c. Further, Foundry may, at its sole discretion, limit, suspend or terminate User's access to the Pool and Service immediately and without advance notice, if:
                        </Typography>
                        <Box sx={{ pl: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                i. User becomes subject to bankruptcy/insolvency proceedings;
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                ii. User liquidates, dissolves, terminates, or suspends its business;
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                iii. User breaches this Service Agreement; or
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                iv. User performs any act or omission that materially impacts its ability to adhere to the Service Agreement, including but not limited to: (i) loss of any required regulatory approvals or licenses, (ii) material adverse change in User's financial condition, or (iii) User's engagement in fraudulent or illegal activities.
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        14. Force Majeure
                    </Typography>
                    <Box sx={{ pl: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            a. Foundry shall not be liable for any non-performance of its obligations pursuant to the Service Agreement if such non-performance is caused by a Force
                            Majeure event. In case of a Force Majeure event, Foundry has the right to suspend or terminate its services immediately under the Service Agreement.
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            "Force Majeure" events shall mean any event or circumstance, or any combination of events or circumstances which are beyond the control of Foundry.
                            Such events or circumstances shall include, but are not limited to events or occurrences that delay, prevent or hinder Foundry from performing such obligations, war,
                            armed conflict, terrorist activities, acts of sabotage, blockade, fire, lightning, acts of God, national strikes, riots, insurrections, civil commotions, quarantine
                            restrictions, epidemics, pandemics, earthquakes, landslides, avalanches, floods, hurricanes, explosions, and regulatory, administrative or similar action or delays to
                            take actions of any governmental authority.
                        </Typography>
                    </Box>
                    <Box component="img" src={feeStructureImg} alt="Fee Schedule" sx={{ width: '638px', maxWidth: '100%', mt: 4 }} />
                </Box>
            </DialogContent>

            <DialogActions sx={{
                backgroundColor: 'var(--neutral-3)',
                p: { xs: '16px', sm: '24px' },
                m: 0,
                justifyContent: "space-between",
                minHeight: '80px',
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 2,
                overflowX: 'hidden',
                maxWidth: '100%',
                '& > :not(style) + :not(style)': {
                    ml: 0
                }
            }}>
                <FormControlLabel
                    sx={{
                        width: '100%',
                        color: hasScrolledToBottom.has(selectedTab) || agreedToTerms.has(selectedTab) ? "inherit" : "var(--color-text-danger)",
                        minHeight: '48px',
                        m: 0,
                        alignItems: 'flex-start'
                    }}
                    control={
                        <Checkbox
                            checked={boxChecked.has(selectedTab)}
                            onChange={() => handleToggleSet(selectedTab)}
                            disabled={!hasScrolledToBottom.has(selectedTab) && !agreedToTerms.has(selectedTab)}
                            sx={{ pt: 0 }}
                        />
                    }
                    label={hasScrolledToBottom.has(selectedTab) || agreedToTerms.has(selectedTab) ? "By checking this box, I confirm that I have read the Terms of Service. I represent and warrant that I am authorized to accept these terms on behalf of the company, organization, or other entity I represent, and that such entity will be bound by these terms." : "Please scroll to bottom"}
                />
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    width: '100%',
                    justifyContent: 'flex-end',
                    pr: { xs: 0, sm: '16px' }
                }}>
                    <Button
                        onClick={onClose}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            lineHeight: "20px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                        }}
                        style={{
                            borderRadius: "var(--radius-xs, 4px)",
                            border: "1px solid var(--Component-colors-Components-Buttons-Secondary-button-secondary-border, #364152)",
                            background: "var(--Component-colors-Components-Buttons-Secondary-button-secondary-bg, #121926)",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 10px !important"
                            },
                            color: "#e6e5e8",
                            minWidth: 85,
                        }}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        component="a"
                        href={tosDocument}
                        download="Foundry_USA_Pool_Service_Agreement.pdf"
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            lineHeight: "20px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            '& .arrowLeft, & .arrowRight': {
                                transform: 'translate(0px, 0px)',
                                transition: 'all 0.5s ease',
                            },
                            '&:hover .arrowLeft, &:hover .arrowRight': {
                                transform: 'translate(0px, 3px)',
                                transition: 'all 0.5s ease',
                            },
                        }}
                        style={{
                            borderRadius: "var(--radius-xs, 4px)",
                            border: "1px solid var(--Component-colors-Components-Buttons-Secondary-button-secondary-border, #364152)",
                            background: "var(--Component-colors-Components-Buttons-Secondary-button-secondary-bg, #121926)",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 10px !important"
                            },
                            color: "#e6e5e8",
                            minWidth: 85,
                        }}
                        variant="contained"
                    >
                        <SvgIcon sx={{ width: '20px', height: '20px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path className="arrowLeft" d="M12 13v8l-4-4" />
                                <path className="arrowRight" d="m12 21 4-4" />
                                <path d="M4.393 15.269A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.436 8.284" />
                            </svg>
                        </SvgIcon>
                        Download
                    </Button>
                    <Button
                        disabled={!boxChecked.has(selectedTab) || agreedToTerms.has(selectedTab)}
                        sx={{
                            opacity: boxChecked.has(selectedTab) && !agreedToTerms.has(selectedTab) ? 1 : 0.2,
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            lineHeight: "20px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                        }}
                        style={{
                            borderRadius: "var(--radius-xs, 4px)",
                            border: "1px solid #00B161",
                            background: "#00B161",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            "& .MuiOutlinedInput-input": {
                                padding: "8px 10px !important"
                            },
                            color: "#e6e5e8",
                            minWidth: 85,
                        }}
                        onClick={() => handleAgreeClick(selectedTab)}
                        variant="contained"
                    >
                        I agree
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default TermsOfServiceDialog;